import React, { createContext, useContext, useEffect, useState } from "react";
import {
  GoogleAuthProvider, signInWithPopup, signInWithRedirect, getRedirectResult,
  signOut, onAuthStateChanged,
} from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { auth, db } from "./firebase";

const Ctx = createContext(null);
export const useSession = () => useContext(Ctx);

// Popups are unreliable on phones and blocked by many browsers; redirect works
// everywhere. Try the popup for the desktop nicety, fall back to redirect the
// moment the environment refuses it — and always surface the error, because a
// sign-in button that fails silently reads as "the sign in is not opening".
const REDIRECT_FALLBACK = new Set([
  "auth/popup-blocked",
  "auth/operation-not-supported-in-this-environment",
  "auth/cancelled-popup-request",
  "auth/internal-error",
]);

const FRIENDLY = {
  "auth/operation-not-allowed":
    "Google sign-in is not enabled on the Firebase project yet. In the Firebase console: Authentication → Sign-in method → Google → Enable.",
  "auth/unauthorized-domain":
    "This domain isn't authorised for sign-in. In the Firebase console: Authentication → Settings → Authorized domains → add this site's domain.",
  "auth/configuration-not-found":
    "Authentication has not been set up on this Firebase project. Open Authentication in the Firebase console and press Get started, then enable Google.",
  "auth/network-request-failed":
    "Network problem reaching Google. Check the connection and try again.",
  "auth/popup-closed-by-user": "The sign-in window was closed before finishing.",
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [ready, setReady] = useState(false);
  const [authError, setAuthError] = useState(null);
  const [signingIn, setSigningIn] = useState(false);

  useEffect(() => {
    // Completes a redirect-based sign-in when the page comes back.
    getRedirectResult(auth).catch((e) =>
      setAuthError(FRIENDLY[e.code] ?? `${e.code ?? "auth-error"}: ${e.message}`)
    );
    return onAuthStateChanged(auth, async (u) => {
      setUser(u);
      if (u) {
        try {
          await u.getIdToken(true); // pick up a freshly synced role claim
          const snap = await getDoc(doc(db, "users", u.uid));
          setProfile(snap.exists() ? { id: u.uid, ...snap.data() } : null);
        } catch {
          setProfile(null);
        }
      } else {
        setProfile(null);
      }
      setReady(true);
    });
  }, []);

  const signIn = async () => {
    setAuthError(null);
    setSigningIn(true);
    const provider = new GoogleAuthProvider();
    try {
      await signInWithPopup(auth, provider);
    } catch (e) {
      if (REDIRECT_FALLBACK.has(e.code)) {
        try {
          await signInWithRedirect(auth, provider); // leaves the page
          return;
        } catch (e2) {
          setAuthError(FRIENDLY[e2.code] ?? `${e2.code ?? "auth-error"}: ${e2.message}`);
        }
      } else {
        setAuthError(FRIENDLY[e.code] ?? `${e.code ?? "auth-error"}: ${e.message}`);
      }
    } finally {
      setSigningIn(false);
    }
  };

  const refresh = async () => {
    const u = auth.currentUser;
    if (!u) return;
    await u.getIdToken(true);
    const snap = await getDoc(doc(db, "users", u.uid));
    setProfile(snap.exists() ? { id: u.uid, ...snap.data() } : null);
  };

  const value = {
    user, profile, ready, authError, signingIn, refresh,
    role: profile?.role ?? "anon",
    isStaff: ["master", "director", "moderator", "lead"].includes(profile?.role),
    isSenior: ["director", "lead"].includes(profile?.role),
    signIn,
    signOut: () => signOut(auth),
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
